import type { KybAuditFilters } from "@/core/kyb/entities/KybCase";

// VITE_BACKEND_BASE_URL already includes the /api/v1 base path, so endpoint
// paths must be relative to it (matching customerPurgeEndpoints).
const kybEndpoints = {
  openOrGetCase: () => `/compliance/kyb/legal-entity-cases`,
  submissionReadiness: (caseId: string) => `/compliance/kyb/legal-entity-cases/${caseId}/submission-readiness`,
  caseByCustomer: (businessKey: string) =>
    `/compliance/kyb/legal-entity-cases/by-customer/${encodeURIComponent(businessKey)}`,
  submitCase: (caseId: string) =>
    `/compliance/kyb/legal-entity-cases/${caseId}/submit`,
  instructionComments: (caseId: string) =>
    `/compliance/kyb/legal-entity-cases/${caseId}/instruction/comments`,
  documentTypes: () => "/bank/types-piece?page=0&size=500",
  complementDocumentTypes: (includeInactive: boolean) =>
    `/compliance/kyb/complement-document-types${includeInactive ? "?includeInactive=true" : ""}`,
  complementDocumentType: (code: string) => `/compliance/kyb/complement-document-types/${encodeURIComponent(code)}`,
  complementDocumentTypeStatus: (code: string) =>
    `/compliance/kyb/complement-document-types/${encodeURIComponent(code)}/status`,
  instructionSummary: (caseId: string) =>
    `/compliance/kyb/legal-entity-cases/${caseId}/instruction/summary`,
  instructionAnalysts: (caseId: string) =>
    `/compliance/kyb/legal-entity-cases/${caseId}/instruction/analysts`,
  instructionAssignment: (caseId: string) =>
    `/compliance/kyb/legal-entity-cases/${caseId}/instruction/assignment`,
  instructionComplementRequests: (caseId: string) =>
    `/compliance/kyb/legal-entity-cases/${caseId}/instruction/complement-requests`,
  instructionReopen: (caseId: string) =>
    `/compliance/kyb/legal-entity-cases/${caseId}/instruction/reopen`,
  instructionReady: (caseId: string) =>
    `/compliance/kyb/legal-entity-cases/${caseId}/instruction/ready`,
  screeningHits: (caseId: string) =>
    `/compliance/kyb/legal-entity-cases/${caseId}/screening-hits`,
  entityScreeningHit: (caseId: string) =>
    `/compliance/kyb/legal-entity-cases/${caseId}/entity-screening-hit`,
  entityScreeningHitQualification: (caseId: string) =>
    `/compliance/kyb/legal-entity-cases/${caseId}/entity-screening-hit/qualification`,
  screeningHitQualification: (caseId: string, relatedPersonId: string) =>
    `/compliance/kyb/legal-entity-cases/${caseId}/screening-hits/${relatedPersonId}/qualification`,
  instructionValidationMfaChallenge: (caseId: string) =>
    `/compliance/kyb/legal-entity-cases/${caseId}/instruction/validation/mfa-challenge`,
  instructionValidation: (caseId: string) =>
    `/compliance/kyb/legal-entity-cases/${caseId}/instruction/validation`,
  decision: (caseId: string) =>
    `/compliance/kyb/legal-entity-cases/${caseId}/decision`,
  decisionEligibility: (caseId: string) =>
    `/compliance/kyb/legal-entity-cases/${caseId}/decision/eligibility`,
  riskSnapshots: (caseId: string) => `/compliance/kyb/legal-entity-cases/${caseId}/risk/snapshots`,
  riskConfig: () => `/compliance/kyb/risk-config`,
  risk: (caseId: string) => `/compliance/kyb/legal-entity-cases/${caseId}/risk`,
  screeningSummary: (caseId: string) =>
    `/compliance/kyb/legal-entity-cases/${caseId}/screening-summary`,
  personVerificationProof: (caseId: string, relatedPersonId: string) =>
    `/compliance/kyb/legal-entity-cases/${caseId}/representatives/${relatedPersonId}/verification/proof`,
  complementRequests: (caseId: string) => `/compliance/kyb/legal-entity-cases/${caseId}/complement-requests`,
  personVerifications: (caseId: string) =>
    `/compliance/kyb/legal-entity-cases/${caseId}/representatives/verification`,
  personVerification: (caseId: string, relatedPersonId: string) =>
    `/compliance/kyb/legal-entity-cases/${caseId}/representatives/${relatedPersonId}/verification`,
  closure: (caseId: string) => `/compliance/kyb/legal-entity-cases/${caseId}/closure`,
  decisionMfaChallenge: (caseId: string) =>
    `/compliance/kyb/legal-entity-cases/${caseId}/decision/mfa-challenge`,
  decisionCbsOpening: (caseId: string) =>
    `/compliance/kyb/legal-entity-cases/${caseId}/decision/cbs-opening`,
  decisionCbsOpeningRetry: (caseId: string) =>
    `/compliance/kyb/legal-entity-cases/${caseId}/decision/cbs-opening/retry`,
  auditFields: (caseId: string) =>
    `/compliance/kyb/legal-entity-cases/${caseId}/audit/fields`,
  caseAudit: (caseId: string, page?: number, size?: number, filters?: KybAuditFilters) => {
    const params = new URLSearchParams();
    if (filters?.fieldCode) {
      params.set("fieldCode", filters.fieldCode);
    }
    if (filters?.actor) {
      params.set("actor", filters.actor);
    }
    if (filters?.source) {
      params.set("source", filters.source);
    }
    // les bornes sont des jours : la première commence à minuit, la seconde finit à la dernière seconde du jour
    if (filters?.from) {
      params.set("from", `${filters.from}T00:00:00Z`);
    }
    if (filters?.to) {
      params.set("to", `${filters.to}T23:59:59Z`);
    }
    if (page !== undefined) {
      params.set("page", String(page));
    }
    if (size !== undefined) {
      params.set("size", String(size));
    }
    const query = params.toString();
    const suffix = query ? `?${query}` : "";
    return `/compliance/kyb/legal-entity-cases/${caseId}/audit${suffix}`;
  },
  documentChecklist: (caseId: string) =>
    `/compliance/kyb/legal-entity-cases/${caseId}/documents`,
  documentChecklistItem: (caseId: string, caseDocumentId: string) =>
    `/compliance/kyb/legal-entity-cases/${caseId}/documents/${caseDocumentId}`,
  documentUpload: (caseId: string) =>
    `/compliance/kyb/legal-entity-cases/${caseId}/documents/upload`,
  documentDownload: (caseId: string, personDocumentId: string) =>
    `/compliance/kyb/legal-entity-cases/${caseId}/documents/${personDocumentId}/download`,
  entityRegistrations: (caseId: string) =>
    `/compliance/kyb/legal-entity-cases/${caseId}/registrations`,
  entityRegistration: (caseId: string, registrationId: string) =>
    `/compliance/kyb/legal-entity-cases/${caseId}/registrations/${registrationId}`,
  entityRegistrationVerification: (caseId: string, registrationId: string) =>
    `/compliance/kyb/legal-entity-cases/${caseId}/registrations/${registrationId}/verification`,
  relatedPersonScreeningRelaunch: (caseId: string, relatedPersonId: string) =>
    `/compliance/kyb/legal-entity-cases/${caseId}/related-persons/${relatedPersonId}/screening/relaunch`,
  screeningRelaunchPending: (caseId: string) =>
    `/compliance/kyb/legal-entity-cases/${caseId}/screening/relaunch-pending`,
  entityRegistrationProof: (caseId: string, registrationId: string) =>
    `/compliance/kyb/legal-entity-cases/${caseId}/registrations/${registrationId}/proof`,
  entityRegistrationProofDownload: (caseId: string, registrationId: string, proofDocumentId: string) =>
    `/compliance/kyb/legal-entity-cases/${caseId}/registrations/${registrationId}/proof/${proofDocumentId}/download`,
  entityRegistrationHistory: (caseId: string) =>
    `/compliance/kyb/legal-entity-cases/${caseId}/registrations/historique`,
  relationRoles: (ecran?: "REPRESENTANTS" | "ACTIONNARIAT_UBO") =>
    ecran
      ? `/compliance/kyb/relation-roles?ecran=${ecran}`
      : `/compliance/kyb/relation-roles`,
  representativeContact: (caseId: string, relatedPersonId: string) =>
    `/compliance/kyb/legal-entity-cases/${caseId}/representatives/${relatedPersonId}/contact`,
  contactChanges: (caseId: string, relatedPersonId: string) =>
    `/compliance/kyb/legal-entity-cases/${caseId}/representatives/${relatedPersonId}/contact-changes`,
  contactChangeMfaChallenge: (caseId: string, relatedPersonId: string) =>
    `/compliance/kyb/legal-entity-cases/${caseId}/representatives/${relatedPersonId}/contact-changes/mfa-challenge`,
  contactChangeApproval: (caseId: string, relatedPersonId: string, requestId: string) =>
    `/compliance/kyb/legal-entity-cases/${caseId}/representatives/${relatedPersonId}/contact-changes/${requestId}/approval`,
  contactChangeRejection: (caseId: string, relatedPersonId: string, requestId: string) =>
    `/compliance/kyb/legal-entity-cases/${caseId}/representatives/${relatedPersonId}/contact-changes/${requestId}/rejection`,
  representatives: (caseId: string, ecran?: "REPRESENTANTS" | "ACTIONNARIAT_UBO") =>
    `/compliance/kyb/legal-entity-cases/${caseId}/representatives${
      ecran === "ACTIONNARIAT_UBO" ? "?ecran=ACTIONNARIAT_UBO" : ""
    }`,
  representativeCandidates: (
    caseId: string,
    lastName: string,
    firstName: string,
    birthDate?: string,
  ) => {
    const params = new URLSearchParams({ lastName, firstName });
    if (birthDate) {
      params.set("birthDate", birthDate);
    }
    return `/compliance/kyb/legal-entity-cases/${caseId}/representatives/candidates?${params.toString()}`;
  },
  representativeRoles: (
    caseId: string,
    relatedPersonId: string,
    ecran?: "REPRESENTANTS" | "ACTIONNARIAT_UBO",
  ) =>
    `/compliance/kyb/legal-entity-cases/${caseId}/representatives/${relatedPersonId}/roles${
      ecran === "ACTIONNARIAT_UBO" ? "?ecran=ACTIONNARIAT_UBO" : ""
    }`,
  representativeRole: (
    caseId: string,
    relatedPersonId: string,
    personRoleId: string,
  ) =>
    `/compliance/kyb/legal-entity-cases/${caseId}/representatives/${relatedPersonId}/roles/${personRoleId}`,
  ownershipLegalParties: (caseId: string) =>
    `/compliance/kyb/legal-entity-cases/${caseId}/ownership/legal-parties`,
  ownershipEdges: (caseId: string) =>
    `/compliance/kyb/legal-entity-cases/${caseId}/ownership/edges`,
  ownershipEdge: (caseId: string, ownershipEdgeId: string) =>
    `/compliance/kyb/legal-entity-cases/${caseId}/ownership/edges/${ownershipEdgeId}`,
  ownershipBeneficialOwners: (caseId: string) =>
    `/compliance/kyb/legal-entity-cases/${caseId}/ownership/ubo`,
  ownershipExport: (caseId: string, format: "JSON" | "PDF") =>
    `/compliance/kyb/legal-entity-cases/${caseId}/ownership/export?format=${format}`,
  ownershipOpacity: (caseId: string) =>
    `/compliance/kyb/legal-entity-cases/${caseId}/ownership/opacity`,
  directorFallback: (caseId: string) =>
    `/compliance/kyb/legal-entity-cases/${caseId}/ownership/director-fallback`,
  directorFallbackSubmit: (caseId: string) =>
    `/compliance/kyb/legal-entity-cases/${caseId}/ownership/director-fallback/submit`,
  directorFallbackDecision: (caseId: string) =>
    `/compliance/kyb/legal-entity-cases/${caseId}/ownership/director-fallback/decision`,
};

export default kybEndpoints;
